// ==========================================================================
// components/PhoneNav.tsx — the phone's bottom bar (10 Oct 2026)
//   James: "a clean camera icon on the bottom of the app with clean nav so its
//   not hiding or pushing anything else. Camera icon leads to options which
//   would be Receipt Capture for now but we may have more things to capture".
//   Home · Money · [camera] · Calendar · More. The camera opens Capture (a list
//   so more kinds can be added); More opens everything else.
// ==========================================================================
import { useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Baby, Camera, CalendarDays, ChefHat, Home, Image, LayoutGrid, ReceiptText, Wallet, X, type LucideIcon } from "lucide-react";
import { useAddReceipts } from "@/lib/receipts";
import { useAddRecipePhoto } from "@/lib/kitchen";
import { setPendingPhotos } from "@/lib/journal";
import { usePeople } from "@/lib/household";
import { NAV } from "./AppShell";

function BottomSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-40 flex items-end bg-black/40 lg:hidden" onClick={onClose}>
      <div className="w-full rounded-t-2xl bg-surface px-5 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-3 shadow-soft" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-line" />
        <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">{title}</h2><button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1 text-ink-muted"><X className="h-5 w-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

function Tab({ to, icon: Icon, label, end }: { to: string; icon: LucideIcon; label: string; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 pb-1.5 pt-2 text-[11px] ${isActive ? "font-semibold text-primary" : "text-ink-muted"}`}>
      {({ isActive }) => (<><span className={`flex h-7 w-12 items-center justify-center rounded-full ${isActive ? "bg-primary/10" : ""}`}><Icon className="h-5 w-5" /></span>{label}</>)}
    </NavLink>
  );
}

export default function PhoneNav() {
  const nav = useNavigate(); const loc = useLocation();
  const [sheet, setSheet] = useState<"capture" | "more" | null>(null);
  const camera = useRef<HTMLInputElement>(null); const library = useRef<HTMLInputElement>(null);
  const add = useAddReceipts(); const addRecipe = useAddRecipePhoto();
  const recipePic = useRef<HTMLInputElement>(null); const journalPic = useRef<HTMLInputElement>(null);
  const kid = usePeople().data?.find((p) => p.role === "child");
  const more = NAV.filter((n) => !["/", "/money", "/calendar"].includes(n.to));
  const moreActive = more.some((n) => loc.pathname.startsWith(n.to));

  const take = (files: FileList | null) => {
    if (!files?.length) return;
    setSheet(null);
    add.mutate(Array.from(files));
    nav("/money/receipts");
  };
  // A journal moment (FF-053): the photos go to the journal screen, which opens a new page with them.
  const takeMoment = (files: FileList | null) => {
    if (!files?.length) return;
    setSheet(null);
    setPendingPhotos(Array.from(files));
    nav("/journal?add=1");
  };
  // A recipe from a photo or screenshot (FF-051): saved as a photo, opened in the book, read only if asked.
  const takeRecipe = (files: FileList | null) => {
    const f = files?.[0]; if (!f) return;
    setSheet(null);
    addRecipe.mutate(f, { onSuccess: (id) => nav(`/kitchen/recipes?open=${id}`) });
    nav("/kitchen/recipes");
  };

  return (
    <>
      <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ""; }} />
      <input ref={journalPic} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { takeMoment(e.target.files); e.target.value = ""; }} />
      <input ref={recipePic} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { takeRecipe(e.target.files); e.target.value = ""; }} />
      <input ref={library} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ""; }} />

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
        <div className="flex items-end">
          <Tab to="/" icon={Home} label="Home" end />
          <Tab to="/money" icon={Wallet} label="Money" />
          <div className="flex flex-1 justify-center">
            <button type="button" aria-label="Capture" onClick={() => setSheet("capture")}
              className="mb-1.5 flex h-14 w-14 -translate-y-3 items-center justify-center rounded-full bg-primary text-white shadow-soft ring-4 ring-surface active:scale-95">
              <Camera className="h-6 w-6" />
            </button>
          </div>
          <Tab to="/calendar" icon={CalendarDays} label="Calendar" />
          <button type="button" onClick={() => setSheet("more")} className={`flex flex-1 flex-col items-center gap-0.5 pb-1.5 pt-2 text-[11px] ${moreActive ? "font-semibold text-primary" : "text-ink-muted"}`}>
            <span className={`flex h-7 w-12 items-center justify-center rounded-full ${moreActive ? "bg-primary/10" : ""}`}><LayoutGrid className="h-5 w-5" /></span>More
          </button>
        </div>
      </nav>

      {sheet === "capture" && (
        <BottomSheet title="Capture" onClose={() => setSheet(null)}>
          <div className="space-y-2">
            <button type="button" onClick={() => camera.current?.click()} className="flex w-full items-center gap-4 rounded-xl bg-primary/10 p-4 text-left">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white"><ReceiptText className="h-5 w-5" /></span>
              <span><span className="block font-semibold">Receipt</span><span className="text-sm text-ink-muted">Take a photo. It lands in Receipts, ready to read and match.</span></span>
            </button>
            <button type="button" onClick={() => library.current?.click()} className="flex w-full items-center gap-4 rounded-xl bg-surface-raised p-4 text-left">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface text-ink"><Image className="h-5 w-5" /></span>
              <span><span className="block font-semibold">Receipt from photos or files</span><span className="text-sm text-ink-muted">Pick one or more you already have.</span></span>
            </button>
            {kid && <button type="button" onClick={() => journalPic.current?.click()} className="flex w-full items-center gap-4 rounded-xl bg-chart-3/10 p-4 text-left" style={{ background: "rgb(var(--chart-3) / 0.1)" }}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-chart-3 text-white"><Baby className="h-5 w-5" /></span>
              <span><span className="block font-semibold">A moment for the journal</span><span className="text-sm text-ink-muted">{kid ? `Pick photos of ${kid.display_name}. They go on a new page in the journal.` : "Pick photos. They go on a new page in the journal."}</span></span>
            </button>}
            <button type="button" onClick={() => recipePic.current?.click()} className="flex w-full items-center gap-4 rounded-xl bg-surface-raised p-4 text-left">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-chart-2/15 text-chart-2"><ChefHat className="h-5 w-5" /></span>
              <span><span className="block font-semibold">Recipe</span><span className="text-sm text-ink-muted">A photo of a cookbook page or a screenshot. It goes in the recipe book.</span></span>
            </button>
          </div>
          <p className="mt-4 text-center text-xs text-ink-muted">More things to capture will show up here.</p>
        </BottomSheet>
      )}

      {sheet === "more" && (
        <BottomSheet title="More" onClose={() => setSheet(null)}>
          <div className="grid grid-cols-3 gap-3">
            {more.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setSheet(null)}
                className={({ isActive }) => `flex flex-col items-center gap-2 rounded-xl p-4 text-sm ${isActive ? "bg-primary/10 font-semibold text-primary" : "bg-surface-raised"}`}>
                <n.icon className="h-6 w-6" />{n.label}
              </NavLink>
            ))}
          </div>
        </BottomSheet>
      )}
    </>
  );
}
